import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ManageProductRequests Component
 * Complete product request management with HTML-based PDF export (like FinalOrders)
 */
function ManageProductRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  
  // Filter states
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalAction, setModalAction] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // View Details Modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRequest, setDetailsRequest] = useState(null);

  const uniqueStaff = [...new Set(requests.map(r => r.staff_full_name).filter(Boolean))].sort();
  const uniqueCategories = [...new Set(requests.map(r => r.category).filter(Boolean))].sort();

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, activeTab, searchTerm, selectedStaff, selectedCategory, dateFrom, dateTo, dateFilter]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('ward_stock_token');
      const response = await fetch('https://b1.envicblue.co.uk/api/product_requests.php?action=list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setRequests(data.data || []);
        setFilteredRequests(data.data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to load product requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('ward_stock_token');
      const response = await fetch('https://b1.envicblue.co.uk/api/product_requests.php?action=stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Tab filter
    if (activeTab !== 'all') {
      filtered = filtered.filter(req => req.status === activeTab);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(req =>
        (req.product_name && req.product_name.toLowerCase().includes(search)) ||
        (req.staff_full_name && req.staff_full_name.toLowerCase().includes(search)) ||
        (req.category && req.category.toLowerCase().includes(search))
      );
    }

    // Staff filter
    if (selectedStaff !== 'all') {
      filtered = filtered.filter(req => req.staff_full_name === selectedStaff);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(req => req.category === selectedCategory);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (dateFilter === 'today') {
        filtered = filtered.filter(req => {
          const reqDate = new Date(req.requested_at);
          const reqDay = new Date(reqDate.getFullYear(), reqDate.getMonth(), reqDate.getDate());
          return reqDay.getTime() === today.getTime();
        });
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = filtered.filter(req => new Date(req.requested_at) >= weekAgo);
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        filtered = filtered.filter(req => new Date(req.requested_at) >= monthAgo);
      } else if (dateFilter === 'custom' && dateFrom && dateTo) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter(req => {
          const reqDate = new Date(req.requested_at);
          return reqDate >= from && reqDate <= to;
        });
      }
    }

    setFilteredRequests(filtered);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== 'custom') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const openModal = (request, action) => {
    setSelectedRequest(request);
    setModalAction(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setModalAction('');
    setAdminNotes('');
  };

  const handleSubmit = async () => {
    if (!selectedRequest) {
      alert('No request selected');
      return;
    }
    
    if (modalAction === 'reject' && !adminNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('ward_stock_token');
      const newStatus = modalAction === 'approve' ? 'approved' : 'rejected';
      const notes = adminNotes.trim();
      
      // Include all params in URL for compatibility when POST converts to GET
      const url = `https://b1.envicblue.co.uk/api/product_requests.php?action=update&request_id=${selectedRequest.id}&status=${newStatus}&admin_notes=${encodeURIComponent(notes)}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_id: selectedRequest.id,
          status: newStatus,
          admin_notes: notes
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Request ${modalAction}d successfully!`);
        closeModal();
        fetchRequests();
        fetchStats();
      } else {
        alert(`Failed to ${modalAction}: ${data.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(`Error ${modalAction}ing request`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      const token = localStorage.getItem('ward_stock_token');
      const response = await fetch(`https://b1.envicblue.co.uk/api/product_requests.php?action=delete&request_id=${requestId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Request deleted successfully!');
        fetchRequests();
        fetchStats();
      } else {
        alert(`Failed to delete: ${data.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error deleting request');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStaff('all');
    setSelectedCategory('all');
    setDateFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  // HTML-based PDF Export (same as FinalOrders.js)
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    
    const filterInfo = [];
    if (activeTab !== 'all') filterInfo.push(`Status: ${activeTab}`);
    if (dateFrom && dateTo) filterInfo.push(`Date: ${dateFrom} to ${dateTo}`);
    if (selectedStaff !== 'all') filterInfo.push(`Staff: ${selectedStaff}`);
    if (selectedCategory !== 'all') filterInfo.push(`Category: ${selectedCategory}`);
    if (searchTerm) filterInfo.push(`Search: ${searchTerm}`);
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Product Requests Report - ${new Date().toLocaleDateString()}</title>
        <style>
          @media print {
            @page { margin: 0.5cm; }
            body { margin: 1cm; }
            .no-print { display: none !important; }
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background: #f5f5f5;
            padding: 20px;
          }
          
          .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #667eea;
          }
          
          .logo {
            font-size: 48px;
            margin-bottom: 10px;
          }
          
          h1 {
            color: #667eea;
            font-size: 32px;
            margin-bottom: 10px;
          }
          
          .subtitle {
            color: #666;
            font-size: 16px;
            margin-bottom: 5px;
          }
          
          .filters {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 30px;
            font-size: 14px;
            color: #555;
          }
          
          .filters strong {
            color: #667eea;
          }
          
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin-bottom: 30px;
          }
          
          .stat-card {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 20px;
            border-radius: 10px;
            text-align: center;
          }
          
          .stat-card.pending {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          }
          
          .stat-card.approved {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          }
          
          .stat-card.rejected {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          }
          
          .stat-label {
            font-size: 14px;
            opacity: 0.9;
            margin-bottom: 8px;
          }
          
          .stat-value {
            font-size: 36px;
            font-weight: bold;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            font-size: 13px;
          }
          
          thead {
            background: #667eea;
            color: white;
          }
          
          th {
            padding: 12px 8px;
            text-align: left;
            font-weight: 600;
          }
          
          td {
            padding: 12px 8px;
            border-bottom: 1px solid #e5e7eb;
          }
          
          tbody tr:hover {
            background: #f9fafb;
          }
          
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
          }
          
          .status-pending {
            background: #fef3c7;
            color: #92400e;
          }
          
          .status-approved {
            background: #d1fae5;
            color: #065f46;
          }
          
          .status-rejected {
            background: #fee2e2;
            color: #991b1b;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
          }
          
          .print-button {
            background: #667eea;
            color: white;
            border: none;
            padding: 15px 40px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            margin: 20px auto;
            display: block;
          }
          
          .print-button:hover {
            background: #5568d3;
          }
          
          .no-data {
            text-align: center;
            padding: 60px 20px;
            color: #9ca3af;
            font-size: 18px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">📦</div>
            <h1>Product Requests Report</h1>
            <div class="subtitle">Ward Stock Management System</div>
            <div class="subtitle">Generated: ${new Date().toLocaleString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</div>
          </div>

          <button onclick="window.print()" class="print-button no-print">
            🖨️ Print / Save as PDF
          </button>

          ${filterInfo.length > 0 ? `
            <div class="filters">
              <strong>Active Filters:</strong> ${filterInfo.join(' • ')}
            </div>
          ` : ''}

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-label">Total Requests</div>
              <div class="stat-value">${filteredRequests.length}</div>
            </div>
            <div class="stat-card pending">
              <div class="stat-label">Pending</div>
              <div class="stat-value">${filteredRequests.filter(r => r.status === 'pending').length}</div>
            </div>
            <div class="stat-card approved">
              <div class="stat-label">Approved</div>
              <div class="stat-value">${filteredRequests.filter(r => r.status === 'approved').length}</div>
            </div>
            <div class="stat-card rejected">
              <div class="stat-label">Rejected</div>
              <div class="stat-value">${filteredRequests.filter(r => r.status === 'rejected').length}</div>
            </div>
          </div>

          ${filteredRequests.length === 0 ? `
            <div class="no-data">
              <div style="font-size: 64px; margin-bottom: 20px;">📦</div>
              <div>No product requests found</div>
            </div>
          ` : `
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th>Est. Price</th>
                  <th>Description</th>
                  <th>Requested By</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                ${filteredRequests.map(req => `
                  <tr>
                    <td><strong>#${req.id}</strong></td>
                    <td><strong style="color: #667eea; font-size: 14px;">${req.product_name || '-'}</strong></td>
                    <td>${req.category || '-'}</td>
                    <td>${req.unit || '-'}</td>
                    <td><strong>${req.estimated_price ? '£' + parseFloat(req.estimated_price).toFixed(2) : '-'}</strong></td>
                    <td style="font-size: 12px; max-width: 200px;">${req.description ? req.description.substring(0, 50) + '...' : '-'}</td>
                    <td style="font-size: 12px; color: #666;">${req.staff_full_name || '-'}</td>
                    <td>
                      <span class="status-badge status-${req.status}">
                        ${req.status}
                      </span>
                    </td>
                    <td>${new Date(req.requested_at).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `}

          <div class="footer">
            <p><strong>Ward Stock Management System</strong></p>
            <p>Designed and Developed by Envicblue International</p>
            <p>This is a system-generated report.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Product Name', 'Requested By', 'Email', 'Category', 'Unit', 'Est. Price', 'Status', 'Date'];
    const csvData = filteredRequests.map(req => [
      req.id,
      req.product_name || '-',
      req.staff_full_name || '-',
      req.staff_email || '-',
      req.category || '-',
      req.unit || '-',
      req.estimated_price || '-',
      req.status,
      new Date(req.requested_at).toLocaleString()
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-requests-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{ fontSize: '48px' }}>⏳ Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb', 
      padding: '30px 20px' 
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            color: '#1f2937',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '40px' }}>📦</span>
            Manage Product Requests
          </h1>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>
            Review and manage staff product requests
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px', 
          marginBottom: '30px' 
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '24px',
            borderRadius: '16px',
            color: 'white',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Requests</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.total}</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            padding: '24px',
            borderRadius: '16px',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
            transform: activeTab === 'pending' ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.2s'
          }}
          onClick={() => setActiveTab('pending')}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>⏳ Pending</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.pending}</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '24px',
            borderRadius: '16px',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
            transform: activeTab === 'approved' ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.2s'
          }}
          onClick={() => setActiveTab('approved')}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>✅ Approved</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.approved}</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            padding: '24px',
            borderRadius: '16px',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
            transform: activeTab === 'rejected' ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.2s'
          }}
          onClick={() => setActiveTab('rejected')}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>❌ Rejected</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.rejected}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '30px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '2px'
        }}>
          {['all', 'pending', 'approved', 'rejected'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                background: activeTab === tab ? '#667eea' : 'transparent',
                color: activeTab === tab ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'all' ? '📋 All' : 
               tab === 'pending' ? '⏳ Pending' :
               tab === 'approved' ? '✅ Approved' : '❌ Rejected'}
            </button>
          ))}
        </div>

        {/* Filters & Export */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
              🔍 Filters & Export
            </h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={exportToPDF}
                style={{
                  padding: '10px 20px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                📄 Export PDF
              </button>
              <button
                onClick={exportToCSV}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                📊 Export CSV
              </button>
              <button
                onClick={clearFilters}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🔄 Clear
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            {/* Search */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                🔎 Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Product, staff, category..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Staff Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                👤 Staff Member
              </label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Staff</option>
                {uniqueStaff.map(staff => (
                  <option key={staff} value={staff}>{staff}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                📂 Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Categories</option>
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                📅 Date Range
              </label>
              <select
                value={dateFilter}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151' 
                  }}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151' 
                  }}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div style={{
          background: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <span style={{ fontSize: '16px', color: '#4b5563', fontWeight: '600' }}>
            📊 Showing {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Requests List */}
        {filteredRequests.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '60px 20px',
            textAlign: 'center',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📦</div>
            <h3 style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>No Requests Found</h3>
            <p style={{ color: '#666' }}>Try adjusting your filters</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredRequests.map((request) => (
              <div key={request.id} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      Request #{request.id}
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                      {request.product_name}
                    </h3>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      👤 {request.staff_full_name}
                    </div>
                  </div>
                  <span style={{
                    padding: '8px 16px',
                    background: `${getStatusColor(request.status)}20`,
                    color: getStatusColor(request.status),
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {request.status === 'pending' ? '⏳' : request.status === 'approved' ? '✅' : '❌'} {request.status}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      Category
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
                      {request.category || '-'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      Estimated Price
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
                      {request.estimated_price ? `£${parseFloat(request.estimated_price).toFixed(2)}` : '-'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                      Request Date
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
                      📅 {new Date(request.requested_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  <button
                    onClick={() => {
                      setDetailsRequest(request);
                      setShowDetailsModal(true);
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    👁️ View More
                  </button>
                  
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => openModal(request, 'approve')}
                        style={{
                          padding: '10px 20px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => openModal(request, 'reject')}
                        style={{
                          padding: '10px 20px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        ❌ Reject
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => handleDelete(request.id)}
                    style={{
                      padding: '10px 20px',
                      background: '#f3f4f6',
                      color: '#ef4444',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginLeft: 'auto'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approve/Reject Modal */}
      {showModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#1f2937',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {modalAction === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Product:</p>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {selectedRequest.product_name}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Requested by:</p>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                {selectedRequest.staff_full_name}
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151',
                marginBottom: '8px'
              }}>
                Admin Notes {modalAction === 'reject' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows="4"
                placeholder={modalAction === 'approve' ? 'Optional notes...' : 'Reason for rejection (required)...'}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={closeModal}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: '2px solid #e5e7eb',
                  background: 'white',
                  color: '#4b5563',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || (modalAction === 'reject' && !adminNotes.trim())}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: 'none',
                  background: modalAction === 'approve' ? '#10b981' : '#ef4444',
                  color: 'white',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (submitting || (modalAction === 'reject' && !adminNotes.trim())) ? 'not-allowed' : 'pointer',
                  opacity: (submitting || (modalAction === 'reject' && !adminNotes.trim())) ? 0.5 : 1
                }}
              >
                {submitting ? 'Processing...' : modalAction === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Product Details Modal */}
      {showDetailsModal && detailsRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          overflow: 'auto'
        }}
        onClick={() => setShowDetailsModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '700px',
            width: '100%',
            boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              marginBottom: '30px',
              paddingBottom: '20px',
              borderBottom: '3px solid #667eea'
            }}>
              <div>
                <h2 style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#1f2937',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  📦 Product Request Details
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  Request ID: #{detailsRequest.id}
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>

            {/* Status Badge */}
            <div style={{ marginBottom: '30px', textAlign: 'center' }}>
              <span style={{
                padding: '12px 30px',
                background: `${getStatusColor(detailsRequest.status)}20`,
                color: getStatusColor(detailsRequest.status),
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                display: 'inline-block'
              }}>
                {detailsRequest.status === 'pending' ? '⏳' : detailsRequest.status === 'approved' ? '✅' : '❌'} {detailsRequest.status}
              </span>
            </div>

            {/* Product Information */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '24px',
              borderRadius: '16px',
              marginBottom: '24px',
              color: 'white'
            }}>
              <h3 style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Product Name</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {detailsRequest.product_name || 'N/A'}
              </p>
            </div>

            {/* Details Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                  📂 Category
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {detailsRequest.category || 'Not specified'}
                </div>
              </div>

              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                  📏 Unit
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {detailsRequest.unit || 'Not specified'}
                </div>
              </div>

              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                  💰 Estimated Price
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>
                  {detailsRequest.estimated_price ? `£${parseFloat(detailsRequest.estimated_price).toFixed(2)}` : 'Not specified'}
                </div>
              </div>

              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                  📅 Request Date
                </div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {new Date(detailsRequest.requested_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </div>
              </div>
            </div>

            {/* Description */}
            {detailsRequest.description && (
              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', fontWeight: '600' }}>
                  📝 Description
                </div>
                <div style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                  {detailsRequest.description}
                </div>
              </div>
            )}

            {/* Reason for Request */}
            {detailsRequest.reason_for_request && (
              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '2px solid #fbbf24'
              }}>
                <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '12px', fontWeight: '600' }}>
                  💡 Reason for Request
                </div>
                <div style={{ fontSize: '15px', color: '#78350f', lineHeight: '1.6' }}>
                  {detailsRequest.reason_for_request}
                </div>
              </div>
            )}

            {/* Supplier Suggestion */}
            {detailsRequest.supplier_suggestion && (
              <div style={{
                background: '#dbeafe',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '2px solid #60a5fa'
              }}>
                <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '12px', fontWeight: '600' }}>
                  🏢 Supplier Suggestion
                </div>
                <div style={{ fontSize: '15px', color: '#1e3a8a', lineHeight: '1.6' }}>
                  {detailsRequest.supplier_suggestion}
                </div>
              </div>
            )}

            {/* Staff Information */}
            <div style={{
              background: '#f3f4f6',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <h4 style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px', fontWeight: '600' }}>
                👤 Requested By
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {detailsRequest.staff_full_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>
                    {detailsRequest.staff_email || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Notes */}
            {detailsRequest.admin_notes && (
              <div style={{
                background: detailsRequest.status === 'rejected' ? '#fee2e2' : '#d1fae5',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: `2px solid ${detailsRequest.status === 'rejected' ? '#fca5a5' : '#6ee7b7'}`
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: detailsRequest.status === 'rejected' ? '#991b1b' : '#065f46', 
                  marginBottom: '12px', 
                  fontWeight: '600' 
                }}>
                  📋 Admin Notes
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  color: detailsRequest.status === 'rejected' ? '#7f1d1d' : '#064e3b', 
                  lineHeight: '1.6' 
                }}>
                  {detailsRequest.admin_notes}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              
              {detailsRequest.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openModal(detailsRequest, 'approve');
                    }}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✅ Approve
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openModal(detailsRequest, 'reject');
                    }}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ❌ Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageProductRequests;
